import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Avatar — coral-gradient circle with initials, validated in sketch 005.
 *
 * Per `.claude/skills/sketch-findings-teachable_clone/references/components.md`
 * § Avatar. Four sizes:
 *   - lg: 80px (`size-20`) + text-2xl
 *   - md: 64px (`size-16`) + text-xl — default
 *   - sm: 36px (`size-9`)  + text-sm
 *   - xs: 32px (`size-8`)  + text-xs
 *
 * When `src` is provided and loads successfully, the image overlays the
 * gradient circle. On image error, falls back to gradient + initials (state
 * hook `imageFailed` flips on the <img>'s onError event).
 *
 * Reserved for compact identity-marker use (user, instructor, hero-sidebar
 * author chip). For hero-scale (≥320px) portraits use `CircularPortrait`
 * instead — different visual treatment (white inset ring + larger shadow).
 *
 * NOT MOUNTED in any route in Phase 7. Phase 8+ surfaces (Marketing Landing
 * testimonial author + Profile re-skin) consume.
 */
export type AvatarSize = "lg" | "md" | "sm" | "xs";

export type AvatarProps = {
  fallback: string; // 1–2 initials, shown when there is no src or the image fails
  src?: string;
  size?: AvatarSize; // default: 'md'
  alt?: string; // used on the <img> when src is provided
  className?: string;
};

const SIZE_CLASS: Record<AvatarSize, string> = {
  lg: "size-20 text-2xl",
  md: "size-16 text-xl",
  sm: "size-9 text-sm",
  xs: "size-8 text-xs",
};

export function Avatar({
  fallback,
  src,
  size = "md",
  alt,
  className,
}: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!src && !imageFailed;

  return (
    <span
      data-slot="avatar"
      data-size={size}
      className={cn(
        "relative inline-grid place-items-center overflow-hidden rounded-full text-white font-extrabold tracking-[0.02em] shadow-elev-2 select-none",
        SIZE_CLASS[size],
        className,
      )}
      style={{
        // Peach → coral gradient. Inline-styled because Tailwind v4
        // arbitrary-value `bg-[linear-gradient(...)]` with nested
        // `hsl(var())` doesn't reliably parse; the inline style is the
        // safest cross-version form.
        background:
          "linear-gradient(135deg, hsl(var(--accent)), hsl(var(--primary)))",
      }}
    >
      {showImage && (
        <img
          src={src}
          alt={alt ?? ""}
          onError={() => setImageFailed(true)}
          className="absolute inset-0 size-full object-cover"
          data-testid="avatar-image"
        />
      )}
      {/* Initials always rendered; <img> overlays when present and loaded. */}
      <span aria-hidden={showImage ? "true" : undefined}>{fallback}</span>
    </span>
  );
}
